/**
 * Lock and channel name derivation. PURE — no browser APIs, so it is directly
 * unit-testable in Jest (whose environment has neither `navigator.locks` nor
 * `BroadcastChannel`).
 *
 * Four namespaces, each with its own prefix so a `resourceId` that itself
 * contains a `:` (every real one does — `"turso:guest.db"`) can never collide
 * across them.
 */

/** Prefix for the exclusive Web Lock whose holder IS the leader. */
const ELECTION_LOCK_PREFIX = "haniverse-leader-election:";
/** Prefix for the resource-wide BroadcastChannel (leader ↔ all tabs). */
const BROADCAST_CHANNEL_PREFIX = "haniverse-leader-broadcast:";
/** Prefix for a per-tab BroadcastChannel (leader ↔ one tab). */
const TAB_CHANNEL_PREFIX = "haniverse-leader-tab:";
/** Prefix for the Web Lock a tab holds for its whole lifetime, as a liveness signal. */
const TAB_CLOSE_LOCK_PREFIX = "haniverse-leader-tab-close:";

/**
 * The election lock. Exclusive, never released voluntarily on the happy path.
 * The browser auto-releases it when the holding worker/tab dies — that release
 * is the CORRECTNESS guarantee of the whole design (everything else, including
 * the `pagehide` relinquish, is an optimization).
 */
export function getElectionLockId(resourceId: string): string {
  return `${ELECTION_LOCK_PREFIX}${resourceId}`;
}

/** The resource-wide channel carrying `tab-here` / `leader-here` / `leader-failed` / `event`. */
export function getBroadcastChannelId(resourceId: string): string {
  return `${BROADCAST_CHANNEL_PREFIX}${resourceId}`;
}

/** The per-tab channel carrying `connected` / `rpc-call` / `rpc-return` / `rpc-error`. */
export function getTabChannelId(tabId: string): string {
  return `${TAB_CHANNEL_PREFIX}${tabId}`;
}

/**
 * The lock a tab's main thread holds for its whole life. The leader `request()`s
 * it; the request RESOLVING means that tab is gone, which is how the leader
 * learns about tab death without a heartbeat.
 */
export function getTabCloseLockId(tabId: string): string {
  return `${TAB_CLOSE_LOCK_PREFIX}${tabId}`;
}
